/**
 * Detekcja utknięcia (§8.3) + WORLD_IDLE (§4.3 / WP-V2-QC).
 * Logika czysta, wstrzykiwany zegar. Cooldown per trigger: 45 s.
 */
import type { CompanionIntentId } from '@pieskowo/shared';

export type StuckTrigger =
  | 'MINIGAME_NO_PROGRESS'
  | 'TAP_NOTHING'
  | 'SERVER_DENIED'
  | 'LOCKED_PLACE'
  | 'IDLE_LONG'
  /** 90 s bezczynności w żywym świecie (PLAN-V2 §4.3) — map hint. */
  | 'WORLD_IDLE';

export type StuckHint = {
  trigger: StuckTrigger;
  intentId: CompanionIntentId;
  at: number;
};

export type StuckDetectorOptions = {
  /** ms — domyślnie Date.now */
  now?: () => number;
  cooldownMs?: number;
  /** ≥5 tapów w 10 s bez trafienia */
  tapNothingCount?: number;
  tapNothingWindowMs?: number;
  /** 0 gwiazdek przez 30 s w mini-grze */
  minigameNoProgressMs?: number;
  /** 90 s bez akcji → WORLD_IDLE (i legacy IDLE_LONG intent map via same threshold) */
  idleLongMs?: number;
  /** 3× denied z rzędu */
  serverDeniedStreak?: number;
};

const DEFAULT_COOLDOWN = 45_000;

/** Mapowanie trigger → intent katalogu companionIntents. */
function intentFor(trigger: StuckTrigger): CompanionIntentId {
  return trigger;
}

export class StuckDetector {
  private readonly now: () => number;
  private readonly cooldownMs: number;
  private readonly tapNothingCount: number;
  private readonly tapNothingWindowMs: number;
  private readonly minigameNoProgressMs: number;
  private readonly idleLongMs: number;
  private readonly serverDeniedStreakNeeded: number;

  private lastFired = new Map<StuckTrigger, number>();
  private emptyTaps: number[] = [];
  private lastActionAt: number;
  private minigameActive = false;
  private minigameStars = 0;
  private minigameStartedAt = 0;
  private deniedStreak = 0;

  constructor(opts: StuckDetectorOptions = {}) {
    this.now = opts.now ?? (() => Date.now());
    this.cooldownMs = opts.cooldownMs ?? DEFAULT_COOLDOWN;
    this.tapNothingCount = opts.tapNothingCount ?? 5;
    this.tapNothingWindowMs = opts.tapNothingWindowMs ?? 10_000;
    this.minigameNoProgressMs = opts.minigameNoProgressMs ?? 30_000;
    this.idleLongMs = opts.idleLongMs ?? 90_000;
    this.serverDeniedStreakNeeded = opts.serverDeniedStreak ?? 3;
    this.lastActionAt = this.now();
  }

  private canFire(trigger: StuckTrigger, t: number): boolean {
    const prev = this.lastFired.get(trigger);
    if (prev !== undefined && t - prev < this.cooldownMs) return false;
    return true;
  }

  private fire(trigger: StuckTrigger, t: number): StuckHint | null {
    if (!this.canFire(trigger, t)) return null;
    this.lastFired.set(trigger, t);
    return { trigger, intentId: intentFor(trigger), at: t };
  }

  /** Dowolna sensowna akcja gracza (tap w obiekt, ruch, dialog…). */
  recordAction(): void {
    this.lastActionAt = this.now();
    this.emptyTaps = [];
  }

  /** Tap w pustkę (bez interaktywnego obiektu). */
  recordEmptyTap(): StuckHint | null {
    const t = this.now();
    this.emptyTaps.push(t);
    const windowStart = t - this.tapNothingWindowMs;
    this.emptyTaps = this.emptyTaps.filter((x) => x >= windowStart);
    // idle nie resetujemy całkowicie — pusty tap to nadal „akcja” słaba
    if (this.emptyTaps.length >= this.tapNothingCount) {
      this.emptyTaps = [];
      return this.fire('TAP_NOTHING', t);
    }
    return null;
  }

  startMinigame(): void {
    this.minigameActive = true;
    this.minigameStars = 0;
    this.minigameStartedAt = this.now();
    this.recordAction();
  }

  endMinigame(): void {
    this.minigameActive = false;
    this.minigameStars = 0;
  }

  setMinigameStars(stars: number): void {
    this.minigameStars = stars;
    if (stars > 0) this.recordAction();
  }

  /** Odmowa serwera (np. za mało zł). */
  recordServerDenied(): StuckHint | null {
    const t = this.now();
    this.deniedStreak += 1;
    this.recordAction();
    if (this.deniedStreak >= this.serverDeniedStreakNeeded) {
      this.deniedStreak = 0;
      return this.fire('SERVER_DENIED', t);
    }
    return null;
  }

  recordServerOk(): void {
    this.deniedStreak = 0;
    this.recordAction();
  }

  /** Tap w zamknięty ogród / cudzy dom. */
  recordLockedPlace(): StuckHint | null {
    const t = this.now();
    this.recordAction();
    return this.fire('LOCKED_PLACE', t);
  }

  /**
   * Poll (np. co klatkę / 1 s) — MINIGAME_NO_PROGRESS + WORLD_IDLE (90 s).
   * WORLD_IDLE = V2 żywy świat (PLAN §4.3); IDLE_LONG zostaje w typie dla kompatybilności API.
   */
  tick(): StuckHint | null {
    const t = this.now();

    if (
      this.minigameActive &&
      this.minigameStars === 0 &&
      t - this.minigameStartedAt >= this.minigameNoProgressMs
    ) {
      const h = this.fire('MINIGAME_NO_PROGRESS', t);
      if (h) {
        // nie spamuj — przesuń start jak po fire
        this.minigameStartedAt = t;
        return h;
      }
    }

    if (t - this.lastActionAt >= this.idleLongMs) {
      // V2: WORLD_IDLE (mapa / atrakcja); legacy IDLE_LONG dzielił ten sam próg —
      // jeden fire WORLD_IDLE na próg, bez podwójnego spamu.
      const h = this.fire('WORLD_IDLE', t);
      if (h) {
        this.lastActionAt = t;
        return h;
      }
    }

    return null;
  }

  /** Testy / debug. */
  resetCooldowns(): void {
    this.lastFired.clear();
  }
}
